import React, { useState, useRef, useEffect } from 'react';
import { Box, Stepper, Step, StepLabel, Button, Typography, Container } from '@mui/material';
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import deepEqual from "fast-deep-equal";
import BasicInfoForm from '../../form/BasicInfoForm';
import SelfPromotionForm from '../../form/SelfPromotionForm';
import AchievementForm from '../../form/AchievementForm';
import QualificationForm from '../../form/QualificationForm';
import HumanSkillForm from '../../form/HumanSkillForm';
import { targetFromFormData, memberSkillSheetPathFor } from "../../../utils/skillSheetRouting";
import { normalizeForDirty } from "../../../utils/normalizeForDirty";
import { useDirtyState } from "../../../shared/hooks/useDirtyState";

const steps = ['基本情報', 'ヒューマンスキル', '自己PR', '自己実績', '資格'];
const STEP_QUALIFICATION = steps.indexOf('資格');

export default function SkillSheetEditStepper({ csrfToken }) {
  const navigate = useNavigate();
  const formRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [formAction, setFormAction] = useState(null);
  const [formData, setFormData] = useState(null);
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(false);

  // dirty判定用
  const initialSnapshotRef = useRef(null);
  const [dirty, setDirty] = useState(false);

  const [activeStep, setActiveStep] = useState(0);

  // ステッパーの参照
  const stepperContainerRef = useRef(null);

  // ステップが変更されたときにスクロール位置を調整する
  useEffect(() => {
    if (stepperContainerRef.current) {
      const stepperContainer = stepperContainerRef.current;
      const stepElements = stepperContainer.querySelectorAll('.MuiStep-root');

      if (stepElements[activeStep]) {
        const activeStepElement = stepElements[activeStep];
        const containerWidth = stepperContainer.offsetWidth;
        const stepWidth = activeStepElement.offsetWidth;
        const stepLeft = activeStepElement.offsetLeft;

        // ステップが中央に来るようにスクロール位置を設定
        const scrollPosition = stepLeft - (containerWidth / 2) + (stepWidth / 2);

        stepperContainer.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });
      }
    }
  }, [activeStep]);

  // ステップが変わったら画面を上に戻す
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [activeStep]);

  useEffect(() => {
    setError(false);

    // ?company_user_id=8 とか ?user_id=41 を拾う
    const searchParamsString = searchParams.toString();
    const searchString = searchParamsString ? `?${searchParamsString}` : "";

    fetch(`/skill_sheet_basics/edit.json${searchString}`)
      .then((res) => res.json())
      .then((data) => {
        setFormAction(data.form_action);

        const [first, second] = data.skill_sheet_template?.name
          ? data.skill_sheet_template.name.split("・")
          : [null, null];

        const initial = {
          ...data.skill_sheet_template,
          role: data.role,

          // dirty判定用（DBには送らない）
          name_first: first ?? null,
          name_second: second ?? null,

          skill_sheet_qualification_infos_attributes:
            data.skill_sheet_template?.skill_sheet_qualification_infos || [],
          validationTriggered: false,
        };
        setFormData(initial);
        setLoading(false);  

        // 初期値をスナップショットとして保存（比較用）
        initialSnapshotRef.current = normalizeForDirty(initial);

        // dirty=false を Layout に通知
        setDirty(false);
      })
      .catch((error) => {
        console.error(error);
        setError(true);
        toast.error("読み込みに失敗しました。", { toastId: "fetch-error" });
      });
  }, [searchParams]);

  // formData が更新されたら dirty を再計算する
  useEffect(() => {
    if (!formData) return;

    const initial = initialSnapshotRef.current;
    const current = normalizeForDirty(formData);
    const nextDirty = !deepEqual(initial, current);

    setDirty(nextDirty);
  }, [formData]);

  // dirty が変わったときだけ Layout に通知
  useDirtyState(dirty);

  if (error) {
    return (
      <Box p={4} ml={4}>
        <Typography>
          読み込みに失敗しました。
          <br />
          通信環境をご確認し、再読み込みしてください。
        </Typography>
      </Box>
    );
  }

  // データ未取得時のローディング表示
  if (loading) {
    return (
      <Box p={4} ml={4}>
        <Typography>読み込み中...</Typography>
      </Box>
    );
  }

  // バリデーションフラグをリセット
  const resetValidationErrors = () => {
    setFormData(prev => ({ ...prev, validationTriggered: false }));
  };

  // 基本情報のバリデーション
  const validateBasicInfo = () => {
    const nameEmpty = !formData.name || formData.name.trim() === '';
    const birthdayEmpty = !formData.birthday || formData.birthday.trim() === '';
    const nationalityEmpty = !formData.nationality || formData.nationality.trim() === '';

    // 従業員の場合のみ「氏名」も必須
    const fullNameEmpty = formData.role === "employee" 
      ? !formData.full_name || formData.full_name.trim() === ''
      : false;

    if (nameEmpty || birthdayEmpty || nationalityEmpty || fullNameEmpty) {
      setFormData(prev => ({ ...prev, validationTriggered: true }));
      return false;
    }

    return true;
  };

  // 資格情報のバリデーション
  const validateQualificationInfo = () => {
    const qualifications = formData.skill_sheet_qualification_infos_attributes || [];
    const activeQualifications = qualifications.filter(q => !q._destroy);

    const hasNameErrors = activeQualifications.some(q => !q.name || q.name.trim() === '');
    const hasDateErrors = activeQualifications.some(q =>
      (!q.acquisition_date || q.acquisition_date.trim() === '')
    );

    if (hasNameErrors || hasDateErrors) {
      setFormData(prev => ({ ...prev, validationTriggered: true }));
      return false;
    }

    return true;
  };

  // ステップごとのバリデーションをチェック
  const validateCurrentStep = () => {
    // 基本情報の場合、必須項目のチェックを行う
    if (activeStep === 0) {
      return validateBasicInfo();
    }
    // 資格の場合、資格名と取得日の必須チェックを行う
    else if (activeStep === STEP_QUALIFICATION) {
      return validateQualificationInfo();
    }

    return true;
  };

  // 次のステップに進む
  const handleNext = () => {
    // バリデーションフラグをセットし、画面の表示を更新
    setFormData(prev => ({ ...prev, validationTriggered: true }));

    // ステップごとのバリデーションが通らない場合は何もしない
    if (!validateCurrentStep()) {
      return;
    }

    // バリデーション成功後、次のステップへ
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    // 次のステップに進んだらバリデーションフラグをリセット
    resetValidationErrors();
  };

  // 前のステップに戻る
  const handleBack = () => {
    // 前のステップに戻るときはバリデーションをリセット
    resetValidationErrors();
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // 特定のステップに移動する
  const handleStepClick = (step) => {
    // 現在と同じステップをクリックした場合は何もしない
    if (step === activeStep) {
      return;
    }

    // 前進（次のステップへ進む）場合のみ現在ステップをバリデーション
    if (step > activeStep) {
      setFormData(prev => ({ ...prev, validationTriggered: true }));

      // バリデーションに失敗した場合は移動しない
      if (!validateCurrentStep()) {
        return;
      }
    }

    setActiveStep(step);
    // 移動後にバリデーションフラグをリセット
    resetValidationErrors();
  };

  // 各ステップコンポーネントに渡してフォームデータを更新するハンドラー
  const handleFormChange = (newData) => {
    setFormData((prevData) => {
      // マージする資格を作成
      const mergedData = { ...prevData };

      // 通常のフィールドをマージ
      Object.keys(newData).forEach(key => {
        // 配列の場合（資格情報など）は置き換える
        if (Array.isArray(newData[key]) || key === 'skill_sheet_qualification_infos_attributes') {
          mergedData[key] = newData[key];
        } else {
          mergedData[key] = newData[key];
        }
      });

      return mergedData;
    });

    // バリデーションフラグがfalseの場合はバリデーションフラグをリセット
    if ('validationTriggered' in newData) {
      if (!newData.validationTriggered) {
        // バリデーションをリセット
        resetValidationErrors();
      }
    }
    // バリデーションフラグがtrueの場合はバリデーションを検証
    else if (formData.validationTriggered) {
      // バリデーション表示中の場合は再検証
      validateCurrentStep();
    } else {
      resetValidationErrors();
    }
  };

  // 最終ステップの送信
  const handleSubmit = async (e) => {
    e.preventDefault();

    // バリデーションフラグをtrueにセット
    setFormData(prev => ({ ...prev, validationTriggered: true }));

    // 送信前に最終ステップをバリデーション
    if (!validateCurrentStep()) {
      return;
    }

    try {
      const fd = new FormData(formRef.current);

      const res = await fetch(formAction, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json",
        },
        body: fd,
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok && data.status === "ok") {
        toast.success("基本情報を更新しました。");

        // 現在の formData を "保存済み" としてスナップショット更新
        initialSnapshotRef.current = normalizeForDirty(formData);

        // 保存成功したので dirty をクリア
        setDirty(false);

        // 保存後の遷移先
        const owner = targetFromFormData(formData);
        navigate(memberSkillSheetPathFor(owner));
      } else {
        toast.error("更新に失敗しました。");
        setFormData((prev) => ({ ...prev, validationTriggered: true }));
      }
    } catch (error) {
      toast.error("通信エラーが発生しました。");
      console.error(error);
    }
  };

  // ステップごとの入力フォームを表示
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return <BasicInfoForm formData={formData} onChange={handleFormChange} />;
      case 1:
        return <HumanSkillForm formData={formData} onChange={handleFormChange} />;
      case 2:
        return <SelfPromotionForm formData={formData} onChange={handleFormChange} />;
      case 3:
        return <AchievementForm formData={formData} onChange={handleFormChange} />;
      case 4:
        return <QualificationForm formData={formData} onChange={handleFormChange} />;
      default:
        return '不明なステップ';
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ width: '100%' }}>
        <form id="skill-sheet-form" ref={formRef} action={formAction} method="post" onSubmit={handleSubmit}>
          <input type="hidden" name="_method" value="patch" />
          <input type="hidden" name="authenticity_token" value={csrfToken} />
          {["user_id", "company_user_id"].map((key) =>
            formData?.[key] ? (
              <input key={key} type="hidden" name={key}  value={formData[key]} />
            ) : null
          )}

          {/* 固定エリア */}
          <Box
            sx={{
              position: "sticky",
              top: { xs: "45px", sm: "63px" },
              zIndex: 20,
              backgroundColor: "#F4F7FA",
              boxShadow: 0.5,
              mx: -2,
              px: { xs: 2, sm: 3 },
              pt: { xs: 1, sm: 1.5 },
              pb: 1,
            }}
          >
            {/* 横スクロール */}
            <Box
              sx={{
                width: "100%",
                "@media (max-width: 640px)": {
                  overflowX: "auto",
                  "&::-webkit-scrollbar": {
                    height: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#f1f1f1",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#888",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#555",
                  },
                },
              }}
              ref={stepperContainerRef}
            >
              <Stepper
                activeStep={activeStep}
                nonLinear={true}
                sx={{
                  mb: 4,
                  "@media (max-width: 640px)": {
                    width: "150%",
                  },
                  "& .MuiStepIcon-root.Mui-completed": {
                    color: "#4F46E5",
                    "& .MuiStepIcon-completedIcon": {
                      display: "none",
                    },
                    "& text": {
                      fill: "white",
                      display: "block",
                    },
                  },
                  "& .MuiStepIcon-root.Mui-active": {
                    color: "#4F46E5",
                  },
                }}
              >
                {steps.map((label, index) => (
                  <Step key={label} completed={false}>
                    <StepLabel
                      onClick={() => handleStepClick(index)}
                      sx={{
                        cursor: "pointer",
                        "&:hover": {
                          opacity: 0.8,
                        },
                        "& .MuiStepIcon-root": {
                          cursor: "pointer",
                        },
                        "& .MuiStepLabel-label": {
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        },
                      }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
          </Box>

          {activeStep !== steps.length && (
            <Box sx={{ bgcolor: "white", p: 3, borderRadius: 1 }}>

              {/* ステップごとの入力フォームを表示 */}
              {getStepContent(activeStep)}

              <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                {activeStep !== 0 && (
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleBack}
                    sx={{ mr: 1 }}
                  >
                    前へ
                  </Button>
                )}
                <Box sx={{ flex: '1 1 auto' }} />
                {activeStep === steps.length - 1 ? (
                  <Button onClick={handleSubmit} variant="contained" sx={{ bgcolor: '#4F46E5', '&:hover': { bgcolor: '#4338CA' } }}>
                    保存
                  </Button>
                ) : (
                  <Button onClick={handleNext} variant="contained" sx={{ bgcolor: '#4F46E5', '&:hover': { bgcolor: '#4338CA' } }}>
                    次へ
                  </Button>
                )}
              </Box>
            </Box>
          )}

          {/* フォームデータをhidden fieldsとして追加 */}
          <input type="hidden" name="skill_sheet_template[name]" value={formData.name || ''} />
          <input type="hidden" name="skill_sheet_template[full_name]" value={formData.full_name || ''} />
          <input type="hidden" name="skill_sheet_template[birthday]" value={formData.birthday || ''} />
          <input type="hidden" name="skill_sheet_template[gender]" value={formData.gender || ''} />
          <input type="hidden" name="skill_sheet_template[nationality]" value={formData.nationality || ''} />
          <input type="hidden" name="skill_sheet_template[nearest_station_line]" value={formData.nearest_station_line || ''} />
          <input type="hidden" name="skill_sheet_template[nearest_station]" value={formData.nearest_station || ''} />
          <input type="hidden" name="skill_sheet_template[final_academic_background]" value={formData.final_academic_background || ''} />
          <input type="hidden" name="skill_sheet_template[self_promotion]" value={formData.self_promotion || ''} />
          <input type="hidden" name="skill_sheet_template[achievement]" value={formData.achievement || ''} />
          <input type="hidden" name="skill_sheet_template[problem_solving]" value={formData.problem_solving || ''} />
          <input type="hidden" name="skill_sheet_template[communication_skill]" value={formData.communication_skill || ''} />
          <input type="hidden" name="skill_sheet_template[autonomy_and_responsibility]" value={formData.autonomy_and_responsibility || ''} />
          <input type="hidden" name="skill_sheet_template[learning_and_growth]" value={formData.learning_and_growth || ''} />
          <input type="hidden" name="skill_sheet_template[team_contribution]" value={formData.team_contribution || ''} />
          {/* 資格情報のhidden input追加 */}
          {(() => {
            const quals = formData.skill_sheet_qualification_infos_attributes || [];

            const filtered = quals.filter((q) => {
              if (q._destroy) return true;
              return q.name?.trim() && q.acquisition_date?.trim();
            });

            return filtered.map((qual, index) => (
              <React.Fragment key={`${qual.id ?? "new"}-${index}`}>
                {qual._destroy && qual.id && (
                  <>
                    <input
                      type="hidden"
                      name={`skill_sheet_template[skill_sheet_qualification_infos_attributes][${index}][id]`}
                      value={qual.id}
                    />
                    <input
                      type="hidden"
                      name={`skill_sheet_template[skill_sheet_qualification_infos_attributes][${index}][_destroy]`}
                      value="1"
                    />
                  </>
                )}
                {/* 有効なレコードは通常のデータを送信 */}
                {!qual._destroy && (
                  <>
                    <input
                      type="hidden"
                      name={`skill_sheet_template[skill_sheet_qualification_infos_attributes][${index}][name]`}
                      value={qual.name || ''}
                    />
                    <input
                      type="hidden"
                      name={`skill_sheet_template[skill_sheet_qualification_infos_attributes][${index}][acquisition_date]`}
                      value={qual.acquisition_date || ''}
                    />
                    {qual.id && (
                      <input
                        type="hidden"
                        name={`skill_sheet_template[skill_sheet_qualification_infos_attributes][${index}][id]`}
                        value={qual.id}
                      />
                    )}
                  </>
                )}
              </React.Fragment>
            ));
          })()}
        </form>
      </Box>
    </Container>
  );
}
